import {
    GET_FINANCIALS_FOR_POLICY,
    RECEIVE_FINANCIALS_FOR_POLICY,
    FAILED_TO_RECEIVE_FINANCIALS_FOR_POLICY,
    LOGOUT
} from "../actions";

// Holds the Financials tab's Current Premium Information (GET api/Applications/{id}/Financials response).
// `financials` starts null and is only ever replaced wholesale on a successful fetch - there's no partial
// merge, since the tab always re-fetches the full DTO on activation (see financialsTabHOC.js), which is
// also how AC11 (data reflects the latest Premium Settings/Case Stage/Damages changes) is satisfied.
//
// `requestedApplicationId` tracks which policy the most recent GET_FINANCIALS_FOR_POLICY was for, so
// RECEIVE_FINANCIALS_FOR_POLICY can tell a stale response apart from the current one. Without it: open
// policy A's Financials tab (request A in flight), navigate to policy B before A's response returns
// (request B fires, financials cleared for B) - if A's response then arrives after B's, it would get
// written into state unconditionally and rendered under B's header, showing an underwriter one policy's
// premium/commission numbers labelled as another's. The clear-on-GET behaviour above only protects
// against showing stale data *while a request is in flight*; it does nothing about *which* in-flight
// request's response lands last, which is what this guards.
const defaultState = {
    fetching: false,
    financials: null,
    error: false,
    requestedApplicationId: null
};

export default function financialsTab(state = defaultState, action = {}) {
    switch (action.type) {
        case GET_FINANCIALS_FOR_POLICY:
            // Clear financials on every new fetch (not just on receive) - financialsTabHOC.js re-fetches on
            // every applicationId change, and without this a policy switch would render the previous
            // policy's data for the duration of the in-flight request. action.payload is the applicationId
            // (see actions/financialsTab.js / financialsTabHOC.js) - record it as the one response we'll
            // accept.
            return {
                ...state,
                fetching: true,
                financials: null,
                error: false,
                requestedApplicationId: action.payload
            };
        case RECEIVE_FINANCIALS_FOR_POLICY: {
            // action.payload.request is the exact request object the cycle built (cycles/financialsTab.js
            // stamps `applicationId` onto it precisely so it survives the round trip here). If it doesn't
            // match the applicationId we most recently asked for, this response belongs to a policy the
            // user has already navigated away from - drop it silently rather than overwriting the current
            // policy's (possibly still-loading) financials with someone else's numbers.
            const respondingApplicationId = action.payload?.request?.applicationId;
            if (respondingApplicationId !== state.requestedApplicationId) {
                return state;
            }
            return {
                ...state,
                fetching: false,
                financials: action.payload.body,
                error: false
            };
        }
        case FAILED_TO_RECEIVE_FINANCIALS_FOR_POLICY: {
            // Same staleness check as the success case, with one difference forced by how @cycle/http
            // attaches the request on the error path (node_modules/@cycle/http/lib/http-driver.js): a
            // genuine non-2xx HTTP response carries it at `err.response.request`, but a network-level
            // failure with no response at all carries it nowhere, so `respondingApplicationId` comes back
            // `undefined` for those. We can only assert staleness when we have positive evidence of a
            // mismatch (a defined id that disagrees with what we asked for) - treating "we don't know
            // whose response this is" as "therefore stale" would silently swallow genuine network errors
            // for the request that's actually still current, which is worse than the rare case of
            // showing a stale error banner.
            const respondingApplicationId =
                action.payload?.request?.applicationId ?? action.payload?.response?.request?.applicationId;
            const isDefinitelyStale =
                respondingApplicationId !== undefined && respondingApplicationId !== state.requestedApplicationId;
            if (isDefinitelyStale) {
                return state;
            }
            // Deliberately leaves the previous `financials` value in place rather than clearing it - a
            // failed re-fetch (e.g. transient network error on tab re-activation) shouldn't blank out data
            // that was already successfully displayed.
            return {
                ...state,
                fetching: false,
                error: true
            };
        }
        // Reset on route change/logout so stale figures don't outlive the policy they belong to.
        case '@@router/LOCATION_CHANGE':
        case LOGOUT:
            return defaultState;
        default:
            return state;
    }
}
